import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@glomopay/utils';

import Spinner from './spinner';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-brand-primary text-text-inverse hover:bg-brand-primary-hover focus-visible:shadow-focus-primary-offset',
        secondary: 'border border-border-brand bg-bg-default text-text-brand hover:bg-brand-primary-subtle focus-visible:shadow-focus-primary',
        destructive: 'bg-bg-error-solid text-text-inverse hover:bg-bg-error-solid-hover focus-visible:shadow-focus-error-offset',
        ghost: 'text-text-brand hover:bg-brand-primary-subtle focus-visible:shadow-focus-primary',
        link: 'text-text-brand underline-offset-4 hover:underline focus-visible:shadow-focus-primary',
        // Carries the tenant's brand fill, which may be a gradient. Hover dims via opacity
        // rather than a colour swap because there is no hover counterpart for an <image>.
        brandFill: 'bg-brand-primary bg-brand-fill text-text-inverse hover:opacity-90 focus-visible:shadow-focus-primary-offset',
      },
      size: {
        sm: 'h-8 px-3 text-xs [&_svg]:size-3',
        default: 'h-10 px-4 text-sm [&_svg]:size-3.5',
        lg: 'h-12 px-5 text-sm [&_svg]:size-3.5',
        icon: 'size-10 [&_svg]:size-3.5',
        inline: 'h-auto p-0',
      },
    },
    compoundVariants: [
      // Pulse has no boxed link component — link is inline navigation, so it never carries a size box.
      { variant: 'link', className: 'h-auto p-0' },
      { variant: 'default', size: 'icon', className: 'bg-interactive-default text-icon-on-interactive hover:bg-interactive-hover' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, loadingText, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} disabled={disabled || isLoading} ref={ref} {...props}>
        {isLoading ? (
          <>
            <Spinner />
            {loadingText ?? children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
